import React, { useState, useEffect } from 'react';
import { Loader2, CheckCircle, XCircle, MapPin } from 'lucide-react';
import { Club, Sport, Country, League } from '../../types/sports';
import { tomtomApi } from '../../services/tomtomApi';

interface ClubFormProps {
  club?: Club | null;
  sports: Sport[];
  countries: Country[];
  leagues: League[];
  onSave: (data: {
    name: string;
    shortName?: string;
    description?: string;
    city?: string;
    stadium?: string;
    stadiumAddress?: string;
    isActive?: boolean;
    sportId?: number;
    countryId?: number;
    leagueId?: number;
  }) => void;
  onCancel: () => void;
  loading?: boolean;
}

const ClubForm: React.FC<ClubFormProps> = ({
  club,
  sports,
  countries,
  leagues,
  onSave,
  onCancel,
  loading = false
}) => {
  const [formData, setFormData] = useState({
    name: '',
    shortName: '',
    description: '',
    city: '',
    stadium: '',
    stadiumAddress: '',
    isActive: true,
    sportId: 0,
    countryId: 0,
    leagueId: 0
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [filteredLeagues, setFilteredLeagues] = useState<League[]>([]);
  const [addressValidation, setAddressValidation] = useState<{
    status: 'idle' | 'loading' | 'success' | 'error';
    formattedAddress?: string;
    latitude?: number;
    longitude?: number;
  }>({ status: 'idle' });

  useEffect(() => {
    if (club) {
      setFormData({
        name: club.name || '',
        shortName: club.shortName || '',
        description: club.description || '',
        city: club.city || '',
        stadium: club.stadium || '',
        stadiumAddress: club.stadiumAddress || '',
        isActive: club.isActive ?? true,
        sportId: club.sportId || 0,
        countryId: club.countryId || 0,
        leagueId: club.leagueId || 0
      });
      // If club already has a validated address, show it
      if (club.stadiumAddress) {
        setAddressValidation({ status: 'success', formattedAddress: club.stadiumAddress });
      }
    }
  }, [club]);

  useEffect(() => {
    // Filter leagues based on selected sport and country
    if (formData.sportId && formData.countryId) {
      const filtered = leagues.filter(league => 
        league.sportId === formData.sportId && league.countryId === formData.countryId
      );
      setFilteredLeagues(filtered);
      
      // Reset league selection if current league is not in filtered list
      if (formData.leagueId && !filtered.some(l => l.id === formData.leagueId)) {
        setFormData(prev => ({ ...prev, leagueId: 0 }));
      }
    } else {
      setFilteredLeagues([]);
      setFormData(prev => ({ ...prev, leagueId: 0 }));
    }
  }, [formData.sportId, formData.countryId, leagues]);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name ist erforderlich';
    } else if (formData.name.length < 2) {
      newErrors.name = 'Name muss mindestens 2 Zeichen lang sein';
    } else if (formData.name.length > 100) {
      newErrors.name = 'Name darf maximal 100 Zeichen lang sein';
    }

    if (formData.shortName && formData.shortName.length > 10) {
      newErrors.shortName = 'Kurzname darf maximal 10 Zeichen lang sein';
    }

    if (formData.description && formData.description.length > 500) {
      newErrors.description = 'Beschreibung darf maximal 500 Zeichen lang sein';
    }

    if (formData.sportId === 0) {
      newErrors.sportId = 'Sportart ist erforderlich';
    }

    if (formData.countryId === 0) {
      newErrors.countryId = 'Land ist erforderlich';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      const submitData = {
        name: formData.name.trim(),
        shortName: formData.shortName.trim() || undefined,
        description: formData.description.trim() || undefined,
        city: formData.city.trim() || undefined,
        stadium: formData.stadium.trim() || undefined,
        stadiumAddress: formData.stadiumAddress.trim() || undefined,
        isActive: formData.isActive,
        sportId: formData.sportId,
        countryId: formData.countryId,
        leagueId: formData.leagueId || undefined
      };
      onSave(submitData);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    // Reset validation and stored address when stadium or city changes
    if (field === 'stadium' || field === 'city') {
      setAddressValidation({ status: 'idle' });
      setFormData(prev => ({ ...prev, [field]: value, stadiumAddress: '' }));
      return;
    }
  };

  const handleValidateAddress = async () => {
    const stadium = formData.stadium.trim();
    const city = formData.city.trim();
    if (!stadium) return;

    const query = city ? `${stadium}, ${city}` : stadium;
    setAddressValidation({ status: 'loading' });

    try {
      const result = await tomtomApi.geocode(query);
      setAddressValidation({
        status: 'success',
        formattedAddress: result.formattedAddress,
        latitude: result.latitude,
        longitude: result.longitude,
      });
      // Store validated address in form data for persistence
      setFormData(prev => ({ ...prev, stadiumAddress: result.formattedAddress || '' }));
    } catch {
      setAddressValidation({ status: 'error' });
    }
  };

  const formStyle: React.CSSProperties = {
    backgroundColor: '#ffffff',
    padding: '24px',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    maxWidth: '600px',
    margin: '0 auto'
  };

  const fieldStyle: React.CSSProperties = {
    marginBottom: '16px'
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: '4px',
    fontWeight: '500',
    color: '#374151'
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    fontSize: '14px',
    boxSizing: 'border-box'
  };

  const errorInputStyle: React.CSSProperties = {
    ...inputStyle,
    borderColor: '#ef4444'
  };

  const selectStyle: React.CSSProperties = {
    ...inputStyle,
    cursor: 'pointer'
  };

  const errorStyle: React.CSSProperties = {
    color: '#ef4444',
    fontSize: '12px',
    marginTop: '4px'
  };

  const buttonContainerStyle: React.CSSProperties = {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '24px'
  };

  const buttonStyle: React.CSSProperties = {
    padding: '8px 16px',
    borderRadius: '4px',
    border: 'none',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s'
  };

  const primaryButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: '#3b82f6',
    color: 'white'
  };

  const secondaryButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: '#f3f4f6',
    color: '#374151'
  };

  const checkboxContainerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  };

  const checkboxStyle: React.CSSProperties = {
    width: '16px',
    height: '16px'
  };

  return (
    <form onSubmit={handleSubmit} style={formStyle}>
      <h3 style={{ marginTop: 0, marginBottom: '24px', color: '#1f2937' }}>
        {club ? 'Verein bearbeiten' : 'Neuen Verein erstellen'}
      </h3>

      <div style={fieldStyle}>
        <label style={labelStyle}>Name *</label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          style={errors.name ? errorInputStyle : inputStyle}
          placeholder="Vereinsname eingeben"
          disabled={loading}
        />
        {errors.name && <div style={errorStyle}>{errors.name}</div>}
      </div>

      <div style={fieldStyle}>
        <label style={labelStyle}>Kurzname</label>
        <input
          type="text"
          value={formData.shortName}
          onChange={(e) => handleInputChange('shortName', e.target.value)}
          style={errors.shortName ? errorInputStyle : inputStyle}
          placeholder="Kurzname (optional)"
          disabled={loading}
        />
        {errors.shortName && <div style={errorStyle}>{errors.shortName}</div>}
      </div>

      <div style={fieldStyle}>
        <label style={labelStyle}>Beschreibung</label>
        <textarea
          value={formData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          style={{
            ...inputStyle,
            ...(errors.description ? { borderColor: '#ef4444' } : {}),
            minHeight: '80px',
            resize: 'vertical' as const
          }}
          placeholder="Beschreibung (optional)"
          disabled={loading}
        />
        {errors.description && <div style={errorStyle}>{errors.description}</div>}
      </div>

      <div style={fieldStyle}>
        <label style={labelStyle}>Stadt</label>
        <input
          type="text"
          value={formData.city}
          onChange={(e) => handleInputChange('city', e.target.value)}
          style={inputStyle}
          placeholder="z.B. Hamburg"
          disabled={loading}
        />
      </div>

      <div style={fieldStyle}>
        <label style={labelStyle}>Stadion</label>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
          <input
            type="text"
            value={formData.stadium}
            onChange={(e) => handleInputChange('stadium', e.target.value)}
            style={{ ...inputStyle, flex: 1 }}
            placeholder="z.B. Millerntorstadion"
            disabled={loading}
          />
          <button
            type="button"
            onClick={handleValidateAddress}
            disabled={loading || !formData.stadium.trim() || addressValidation.status === 'loading'}
            style={{
              ...buttonStyle,
              backgroundColor: addressValidation.status === 'success' ? '#10b981'
                : addressValidation.status === 'error' ? '#ef4444'
                : '#6366f1',
              color: 'white',
              whiteSpace: 'nowrap',
              opacity: (!formData.stadium.trim() || addressValidation.status === 'loading') ? 0.5 : 1,
            }}
          >
            {addressValidation.status === 'loading' ? <><Loader2 size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Prüfe...</>
              : addressValidation.status === 'success' ? <><CheckCircle size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Geprüft</>
              : addressValidation.status === 'error' ? <><XCircle size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Erneut</>
              : <><MapPin size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Adresse prüfen</>}
          </button>
        </div>
        {addressValidation.status === 'success' && (
          <div style={{ fontSize: '12px', color: '#059669', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <CheckCircle size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> TomTom-Adresse aufgelöst
            {addressValidation.latitude && (
              <span style={{ color: '#6b7280' }}>
                ({addressValidation.latitude?.toFixed(4)}, {addressValidation.longitude?.toFixed(4)})
              </span>
            )}
          </div>
        )}
        {addressValidation.status === 'error' && (
          <div style={{ fontSize: '12px', color: '#dc2626', marginTop: '4px' }}>
            <XCircle size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Adresse konnte nicht aufgelöst werden. Bitte Stadionname und Stadt prüfen.
          </div>
        )}
        {addressValidation.status === 'idle' && !formData.stadiumAddress && (
          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
            Wird für die automatische Stadion-Adresse in der Spieltagsplanung verwendet. Bitte mit "Adresse prüfen" validieren.
          </div>
        )}
      </div>

      <div style={fieldStyle}>
        <label style={labelStyle}>Stadionadresse (für Routenberechnung)</label>
        <input
          type="text"
          value={formData.stadiumAddress}
          onChange={(e) => handleInputChange('stadiumAddress', e.target.value)}
          style={{
            ...inputStyle,
            borderColor: formData.stadiumAddress ? '#10b981' : '#d1d5db',
            backgroundColor: formData.stadiumAddress ? '#f0fdf4' : '#ffffff'
          }}
          placeholder="Wird automatisch durch 'Adresse prüfen' befüllt oder manuell eingeben"
          disabled={loading}
        />
        <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
          Diese Adresse wird für die Routenberechnung in der Spieltagsplanung verwendet. Kann manuell überschrieben werden.
        </div>
      </div>

      <div style={fieldStyle}>
        <label style={labelStyle}>Sportart *</label>
        <select
          value={formData.sportId}
          onChange={(e) => handleInputChange('sportId', parseInt(e.target.value))}
          style={errors.sportId ? { ...selectStyle, borderColor: '#ef4444' } : selectStyle}
          disabled={loading}
        >
          <option value={0}>Sportart auswählen</option>
          {sports.map(sport => (
            <option key={sport.id} value={sport.id}>
              {sport.icon ? `${sport.icon} ` : ''}{sport.name}
            </option>
          ))}
        </select>
        {errors.sportId && <div style={errorStyle}>{errors.sportId}</div>}
      </div>

      <div style={fieldStyle}>
        <label style={labelStyle}>Land *</label>
        <select
          value={formData.countryId}
          onChange={(e) => handleInputChange('countryId', parseInt(e.target.value))}
          style={errors.countryId ? { ...selectStyle, borderColor: '#ef4444' } : selectStyle}
          disabled={loading}
        >
          <option value={0}>Land auswählen</option>
          {countries.map(country => (
            <option key={country.id} value={country.id}>
              {country.flag ? `${country.flag} ` : ''}{country.name}
            </option>
          ))}
        </select>
        {errors.countryId && <div style={errorStyle}>{errors.countryId}</div>}
      </div>

      <div style={fieldStyle}>
        <label style={labelStyle}>Liga</label>
        <select
          value={formData.leagueId}
          onChange={(e) => handleInputChange('leagueId', parseInt(e.target.value))}
          style={selectStyle}
          disabled={loading || filteredLeagues.length === 0}
        >
          <option value={0}>Liga auswählen (optional)</option>
          {filteredLeagues.map(league => (
            <option key={league.id} value={league.id}>
              {league.name} (Level {league.level})
            </option>
          ))}
        </select>
        {formData.sportId && formData.countryId && filteredLeagues.length === 0 && (
          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
            Keine Ligen für die ausgewählte Sportart und das Land verfügbar
          </div>
        )}
      </div>

      {club && (
        <div style={fieldStyle}>
          <div style={checkboxContainerStyle}>
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) => handleInputChange('isActive', e.target.checked)}
              style={checkboxStyle}
              disabled={loading}
            />
            <label htmlFor="isActive" style={{ ...labelStyle, marginBottom: 0 }}>
              Aktiv
            </label>
          </div>
        </div>
      )}

      <div style={buttonContainerStyle}>
        <button
          type="button"
          onClick={onCancel}
          style={secondaryButtonStyle}
          disabled={loading}
        >
          Abbrechen
        </button>
        <button
          type="submit"
          style={primaryButtonStyle}
          disabled={loading}
        >
          {loading ? 'Speichern...' : (club ? 'Aktualisieren' : 'Erstellen')}
        </button>
      </div>
    </form>
  );
};

export default ClubForm;
